import React, { useEffect, useMemo, useState } from 'react';
import GenericTemplate from '../components/templates/GenericTemplate';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Divider,
  Chip,
} from '@material-ui/core';
import { Link as RouterLink } from 'react-router-dom';
import { Link } from '@material-ui/core';
import { getNgWords } from 'src/admin/firebase/ngwords';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      padding: theme.spacing(2),
    },
    actionsRow: {
      display: 'flex',
      alignItems: 'center',
      '& > * + *': {
        marginLeft: theme.spacing(1),
      },
    },
    preview: {
      marginTop: theme.spacing(1),
      padding: theme.spacing(2),
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      lineHeight: 1.8,
    },
    highlight: {
      backgroundColor: 'rgba(255, 193, 7, 0.6)',
      borderRadius: 4,
      padding: '0 2px',
    },
    chips: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
      marginTop: theme.spacing(1),
    },
  }),
);

type Hit = {
  word: string;
  start: number;
  end: number;
  ngIndex: number; // ★ これが必須
};

function escapeRegExp(s: string) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function buildRegex(words: string[]) {
  const cleaned = words
    .map((w) => w.trim())
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);

  if (cleaned.length === 0) return null;
  const pattern = cleaned.map(escapeRegExp).join('|');
  return new RegExp(pattern, 'g');
}

function findHits(
  text: string,
  regex: RegExp,
  indexMap: Map<string, number>,
): Hit[] {
  const hits: Hit[] = [];
  regex.lastIndex = 0;
  let m: RegExpExecArray | null;

  while ((m = regex.exec(text)) !== null) {
    const word = m[0];
    const ngIndex = indexMap.get(word) ?? -1;

    hits.push({
      word,
      start: m.index,
      end: m.index + word.length,
      ngIndex,
    });

    if (word.length === 0) regex.lastIndex++;
  }
  return hits;
}

function mergeRanges(hits: Hit[]): Hit[] {
  if (hits.length === 0) return [];
  const sorted = [...hits].sort((a, b) =>
    a.start !== b.start ? a.start - b.start : b.end - a.end,
  );

  const merged: Hit[] = [];
  let cur = sorted[0];

  for (let i = 1; i < sorted.length; i++) {
    const h = sorted[i];
    if (h.start <= cur.end) {
      const useH = h.word.length > cur.word.length; // 長い方を代表に
      cur = {
        word: useH ? h.word : cur.word,
        ngIndex: useH ? h.ngIndex : cur.ngIndex,
        start: cur.start,
        end: Math.max(cur.end, h.end),
      };
    } else {
      merged.push(cur);
      cur = h;
    }
  }
  merged.push(cur);
  return merged;
}

function renderHighlighted(
  text: string,
  ranges: Hit[],
  highlightClass: string,
) {
  if (!text) {
    return (
      <Typography variant="body2" color="textSecondary">
        ここにハイライト結果が表示されます。
      </Typography>
    );
  }
  if (ranges.length === 0) {
    return <Typography variant="body1">{text}</Typography>;
  }

  const nodes: React.ReactNode[] = [];
  let cursor = 0;

  ranges.forEach((r, i) => {
    const before = text.slice(cursor, r.start);
    if (before) nodes.push(<span key={`b-${i}`}>{before}</span>);

    const hit = text.slice(r.start, r.end);
    nodes.push(
      <span
        key={`h-${i}`}
        className={highlightClass}
        title={`NG候補: ${r.word} / ${r.start + 1}文字目`}
      >
        {hit}
      </span>,
    );

    cursor = r.end;
  });

  const after = text.slice(cursor);
  if (after) nodes.push(<span key="after">{after}</span>);

  return <Typography variant="body1">{nodes}</Typography>;
}

function snippetAround(text: string, start: number, end: number, pad = 12) {
  const s = Math.max(0, start - pad);
  const e = Math.min(text.length, end + pad);
  const head = s > 0 ? '…' : '';
  const tail = e < text.length ? '…' : '';
  return head + text.slice(s, e) + tail;
}

const NgWordCheckerPage: React.FC = () => {
  const classes = useStyles();

  const [ngWords, setNgWords] = useState<string[]>([]);
  const [text, setText] = useState('');

  const ngIndexMap = useMemo(() => {
    const m = new Map<string, number>();
    ngWords.forEach((w, i) => {
      if (!m.has(w)) m.set(w, i); // 重複があっても最初を採用
    });
    return m;
  }, [ngWords]);

  // 「チェック」押したタイミングでのみ反映される入力
  const [checkedText, setCheckedText] = useState('');

  // チェック結果
  const [hitList, setHitList] = useState<Hit[]>([]);
  const [mergedRanges, setMergedRanges] = useState<Hit[]>([]);

  useEffect(() => {
    (async () => {
      const list = await getNgWords();
      setNgWords(list ?? []);
    })();
  }, []);

  const regex = useMemo(() => buildRegex(ngWords), [ngWords]);

  const onCheck = () => {
    if (!regex) {
      setCheckedText(text);
      setHitList([]);
      setMergedRanges([]);
      return;
    }

    // チェックボタン押下時だけ計算
    const hits = findHits(text, regex, ngIndexMap);
    const merged = mergeRanges(hits);

    setCheckedText(text);
    setHitList(hits);
    setMergedRanges(merged);
  };

  // 表示用：同じ単語でも複数回出るので、そのまま “出現順” で並べる
  const orderedHits = useMemo(() => {
    return [...hitList].sort((a, b) => a.start - b.start);
  }, [hitList]);

  return (
    <GenericTemplate title="NGワードチェッカー">
      <Box mb={2}>
        <Link component={RouterLink} to="/NgWord">
          ← NGワード管理へ戻る
        </Link>
      </Box>

      <Paper className={classes.container} elevation={2}>
        <Typography variant="body2" color="textSecondary">
          「チェック」ボタン押下時のみNGワード判定します（部分一致）。
        </Typography>

        <Box mt={2}>
          <TextField
            label="チェックしたい文章"
            value={text}
            onChange={(e) => setText(e.target.value)}
            fullWidth
            multiline
            rows={6}
            variant="outlined"
          />
        </Box>

        <Box mt={1} className={classes.actionsRow}>
          <Button variant="contained" color="primary" onClick={onCheck}>
            チェック
          </Button>
          <Button variant="outlined" onClick={() => setText('')}>
            クリア
          </Button>
          <Typography variant="body2" color="textSecondary">
            NGワード数：{ngWords.length}
          </Typography>
        </Box>

        <Box mt={2}>
          <Divider />
        </Box>

        <Box mt={2}>
          <Typography variant="subtitle1">
            判定：{orderedHits.length > 0 ? 'NGワード検知あり' : '検知なし'}
          </Typography>

          {orderedHits.length > 0 && (
            <>
              <Typography variant="body2" color="textSecondary">
                ヒット件数：{orderedHits.length}（同一単語の複数出現含む）
              </Typography>

              <Box mt={1} className={classes.chips}>
                {orderedHits.slice(0, 50).map((h, i) => (
                  <Chip
                    key={`${h.word}-${h.start}-${i}`}
                    size="small"
                    label={`${i + 1}件目:「${h.word}」(登録${h.ngIndex + 1}番目) @ ${h.start + 1}文字目`}
                    title={snippetAround(checkedText || text, h.start, h.end)}
                  />
                ))}
              </Box>

              {orderedHits.length > 50 && (
                <Typography variant="body2" color="textSecondary">
                  ※表示は先頭50件まで（大量ヒット時の重さ対策）
                </Typography>
              )}
            </>
          )}

          <Paper className={classes.preview} variant="outlined">
            {renderHighlighted(checkedText, mergedRanges, classes.highlight)}
          </Paper>
        </Box>
      </Paper>
    </GenericTemplate>
  );
};

export default NgWordCheckerPage;
